import React, { useState } from 'react';
import { api } from '../../../lib/api';
import { Printer, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Modal, L } from './ui';

/* ---------------- AI Letter of employment (iter 145) ---------------- */

function LetterModal({ emp, onClose }) {
  const [instructions, setInstructions] = useState('');
  const [letter, setLetter] = useState(null);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hr/employees/${emp.employee_id}/employment-letter`, { instructions: instructions || null });
      setLetter(data);
      setBody(data.body);
      toast.success('Letter generated — you can edit it before printing.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed.');
    } finally { setBusy(false); }
  };

  const print = () => {
    if (!letter) return;
    const lh = letter.letterhead || {};
    const w = window.open('', '_blank', 'width=800,height=900');
    w.document.write(`<!doctype html><html><head><title>${letter.subject}</title>
      <style>
        body{font-family:Georgia,serif;color:#1a1a1a;max-width:680px;margin:40px auto;padding:0 24px;line-height:1.6}
        .lh{display:flex;align-items:center;gap:16px;border-bottom:3px solid #003D80;padding-bottom:14px;margin-bottom:8px}
        .lh img{height:52px}
        .lh .n{font-size:22px;font-weight:bold;color:#003D80}
        .lh .c{font-size:11px;color:#555}
        .date{text-align:right;font-size:13px;color:#555;margin:14px 0 24px}
        h2{font-size:16px;margin-bottom:18px}
        pre{white-space:pre-wrap;font-family:Georgia,serif;font-size:14px}
      </style></head><body>
      <div class="lh">
        ${lh.logo_url ? `<img src="${lh.logo_url}" alt=""/>` : ''}
        <div><div class="n">${lh.clinic_name || ''}</div>
        <div class="c">${[lh.address, lh.phone, lh.email, lh.website].filter(Boolean).join(' · ')}</div></div>
      </div>
      <div class="date">${letter.date}</div>
      <h2>${letter.subject}</h2>
      <pre>${body.replace(/</g, '&lt;')}</pre>
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  return (
    <Modal onClose={onClose} wide>
      <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><FileText className="w-5 h-5 text-brand"/> Letter of employment — {emp.first_name} {emp.last_name}</h3>
      <p className="text-xs text-fg-muted mb-3">AI-assisted, formatted on your clinic letterhead. Generate, edit the text, then print.</p>
      <L label="Extra instructions for the AI (optional)">
        <input className="input" value={instructions} onChange={e => setInstructions(e.target.value)}
               placeholder="e.g. for a visa application; address it to the Embassy of Canada" data-testid="letter-instructions"/>
      </L>
      <div className="flex justify-end mt-2 mb-3">
        <button className="btn-primary text-xs inline-flex items-center gap-1.5" disabled={busy} onClick={generate} data-testid="letter-generate-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <FileText className="w-3.5 h-3.5"/>}
          {letter ? 'Regenerate' : 'Generate letter'}
        </button>
      </div>
      {letter && (
        <div className="space-y-3" data-testid="letter-preview">
          <div className="border-b-4 border-[#003D80] pb-2 flex items-center gap-3">
            {letter.letterhead?.logo_url && <img src={letter.letterhead.logo_url} alt="" className="h-10"/>}
            <div>
              <div className="font-heading font-bold text-[#003D80]">{letter.letterhead?.clinic_name}</div>
              <div className="text-[10px] text-fg-muted">{[letter.letterhead?.address, letter.letterhead?.phone, letter.letterhead?.email].filter(Boolean).join(' · ')}</div>
            </div>
            <div className="ml-auto text-xs text-fg-muted">{letter.date}</div>
          </div>
          <div className="text-sm font-semibold">{letter.subject}</div>
          <textarea className="input min-h-[280px] !text-sm font-serif" value={body} onChange={e => setBody(e.target.value)} data-testid="letter-body"/>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary text-xs" onClick={onClose}>Close</button>
            <button className="btn-primary text-xs inline-flex items-center gap-1.5" onClick={print} data-testid="letter-print-btn">
              <Printer className="w-3.5 h-3.5"/> Print letter
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export { LetterModal };
